import { useMemo } from 'react';

import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { FiChevronLeft } from 'react-icons/fi';

import { MetaTags } from '@components';
import { useSponsorsQuery, usePartnerCategoriesQuery } from '@hooks';
import { SponsorsData } from '@common/types/api';
import { generateArray } from '@utils';

import * as S from './styles';

interface SponsorGroup {
  categoryId: number | null;
  categoryName: string | null;
  categoryColor: string | null;
  sequence: number;
  sponsors: SponsorsData[];
}

const Sponsors = () => {
  const history = useHistory();

  const sponsorsQuery = useSponsorsQuery(1);
  const categoriesQuery = usePartnerCategoriesQuery();
  const { t } = useTranslation();

  const allSponsors = useMemo(
    () => sponsorsQuery.data?.sponsors ?? [],
    [sponsorsQuery.data?.sponsors],
  );

  const sponsorGroups = useMemo(() => {
    const categories = categoriesQuery.data ?? [];
    const grouped: SponsorGroup[] = [];

    // Group sponsors that have categories
    categories.forEach((cat) => {
      const catSponsors = allSponsors.filter(
        (s) => s.partner_category_id === cat.id,
      );
      if (catSponsors.length > 0) {
        grouped.push({
          categoryId: cat.id,
          categoryName: cat.name,
          categoryColor: cat.color,
          sequence: cat.sequence,
          sponsors: catSponsors,
        });
      }
    });

    // Sort by sequence
    grouped.sort((a, b) => a.sequence - b.sequence);

    // Sponsors without category
    const uncategorized = allSponsors.filter((s) => !s.partner_category_id);
    if (uncategorized.length > 0) {
      grouped.push({
        categoryId: null,
        categoryName: null,
        categoryColor: null,
        sequence: Infinity,
        sponsors: uncategorized,
      });
    }

    return grouped;
  }, [allSponsors, categoriesQuery.data]);

  const hasCategories = sponsorGroups.some((g) => g.categoryId !== null);

  return (
    <S.Container>
      <MetaTags title={t('pages.sponsors.title')} />

      <S.PageHeader>
        <S.PageHeaderLeft>
          <S.BackButton type="button" onClick={() => history.goBack()}>
            <FiChevronLeft size="1.375rem" />
          </S.BackButton>
          <h1>{t('pages.sponsors.title')}</h1>
          {/* {!sponsorsQuery.isLoading && (
            <S.CountBadge>
              {allSponsors.length} {t('pages.sponsors.subTitle')}
            </S.CountBadge>
          )} */}
        </S.PageHeaderLeft>
      </S.PageHeader>

      {sponsorsQuery.isLoading && (
        <S.SponsorGrid>
          {generateArray(6).map((key) => (
            <S.SkeletonCard key={key}>
              <Skeleton height="100%" />
            </S.SkeletonCard>
          ))}
        </S.SponsorGrid>
      )}

      {!sponsorsQuery.isLoading && hasCategories
        ? sponsorGroups.map((group) => (
            <S.CategorySection key={group.categoryId ?? 'uncategorized'}>
              {group.categoryName && (
                <S.CategoryHeader $color={group.categoryColor}>
                  <S.CategoryIcon $color={group.categoryColor}>
                    ◆
                  </S.CategoryIcon>
                  <S.CategoryName $color={group.categoryColor}>
                    {group.categoryName}
                  </S.CategoryName>
                  <S.CategoryLine $color={group.categoryColor} />
                </S.CategoryHeader>
              )}
              <S.SponsorGrid $columns={Math.min(group.sponsors.length, 3)}>
                {group.sponsors.map((sponsor, index) => (
                  <S.SponsorCard
                    key={`sponsor-${sponsor.id}`}
                    $delay={index}
                    onClick={() => history.push(`/sponsors/${sponsor.id}`)}
                  >
                    <S.LogoWrapper>
                      <img src={sponsor.logo} alt={sponsor.name} />
                    </S.LogoWrapper>
                  </S.SponsorCard>
                ))}
              </S.SponsorGrid>
            </S.CategorySection>
          ))
        : !sponsorsQuery.isLoading && (
            <S.SponsorGrid>
              {allSponsors.map((sponsor, index) => (
                <S.SponsorCard
                  key={`sponsor-${sponsor.id}`}
                  $delay={index}
                  onClick={() => history.push(`/sponsors/${sponsor.id}`)}
                >
                  <S.LogoWrapper>
                    <img src={sponsor.logo} alt={sponsor.name} />
                  </S.LogoWrapper>
                </S.SponsorCard>
              ))}
            </S.SponsorGrid>
          )}
    </S.Container>
  );
};

export default Sponsors;
